import { useState } from 'react';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { Link } from 'components/common';
import { Icon } from 'components/common/icons';

import type { NavLinkOrSubheading } from 'types';

interface Props {
  active: string;
  links: NavLinkOrSubheading[];
  preserveBackReference?: boolean;
}

const PageSidebarNav = ({ active, links, preserveBackReference }: Props) => {
  const { theme } = useReduxState();

  const [isOpen, setIsOpen] = useState(false);

  const toggle = (ev) => {
    ev.preventDefault();
    setIsOpen(!isOpen);
  };

  const activeLink = links.find(
    (link) =>
      link.type !== 'subheading' && (link.as === active || link.href === active)
  );

  return (
    <nav className={`nav ${isOpen ? 'is-open' : ''}`}>
      {activeLink && activeLink.type !== 'subheading' ? (
        <button className="toggle" onClick={toggle}>
          {activeLink.icon ? (
            <div className="icon">
              <Icon fixedWidth={true} icon={activeLink.icon} />
            </div>
          ) : activeLink.customIcon ? (
            <Icon
              icon={activeLink.customIcon}
              style={activeLink.customIconStyle}
              type="custom"
            />
          ) : null}

          {activeLink.title ? (
            <span className="link-title">{activeLink.title}</span>
          ) : null}

          {activeLink.unreadItems ? (
            <div
              className="unread-items"
              {...(activeLink.unreadItemsLabel
                ? {
                    'aria-label': activeLink.unreadItemsLabel,
                    'data-tooltip': activeLink.unreadItemsLabel
                  }
                : {})}
            >
              {activeLink.unreadItems}
            </div>
          ) : activeLink.countItems ? (
            <div
              className="count-items"
              {...(activeLink.countItemsLabel
                ? {
                    'aria-label': activeLink.countItemsLabel,
                    'data-tooltip': activeLink.countItemsLabel
                  }
                : {})}
            >
              {activeLink.countItems}

              {activeLink.countItemsUnread ? (
                <div className="count-items-unread">
                  {activeLink.countItemsUnread}
                </div>
              ) : null}
            </div>
          ) : activeLink.successItem ? (
            <div
              aria-label={activeLink.successItem}
              className="success-item"
              data-tooltip={activeLink.successItem}
            >
              <Icon icon="check" />
            </div>
          ) : null}

          <div className="toggle-icon">
            <Icon
              fixedWidth={true}
              icon={isOpen ? 'chevron-up' : 'chevron-down'}
            />
          </div>
        </button>
      ) : null}

      {links.map((link, i) =>
        link.type === 'subheading' ? (
          <div className="link-subheading" key={i}>
            {link.title}
          </div>
        ) : (
          <div
            className={`link-wrapper ${
              link.as === active || link.href === active ? 'active' : ''
            } ${link.isSublink ? 'link-wrapper-sublink' : ''}`}
            key={i}
          >
            <Link
              as={link.as}
              href={link.href}
              style={{ textDecoration: 'none', width: '100%' }}
              {...(preserveBackReference ? { replace: true } : {})}
            >
              <div
                className={`link ${
                  link.as === active || link.href === active ? 'active' : ''
                }`}
              >
                {link.icon ? (
                  <div className="icon">
                    <Icon fixedWidth={true} icon={link.icon} />
                  </div>
                ) : link.customIcon ? (
                  <Icon
                    icon={link.customIcon}
                    style={link.customIconStyle}
                    type="custom"
                  />
                ) : null}
                {link.title ? (
                  <span className="link-title">{link.title}</span>
                ) : null}
                {link.unreadItems ? (
                  <div
                    className="unread-items"
                    {...(link.unreadItemsLabel
                      ? {
                          'aria-label': link.unreadItemsLabel,
                          'data-tooltip': link.unreadItemsLabel
                        }
                      : {})}
                  >
                    {link.unreadItems}
                  </div>
                ) : link.countItems ? (
                  <div
                    className="count-items"
                    {...(link.countItemsLabel
                      ? {
                          'aria-label': link.countItemsLabel,
                          'data-tooltip': link.countItemsLabel
                        }
                      : {})}
                  >
                    {link.countItems}

                    {link.countItemsUnread ? (
                      <div className="count-items-unread">
                        {link.countItemsUnread}
                      </div>
                    ) : null}
                  </div>
                ) : link.successItem ? (
                  <div
                    aria-label={link.successItem}
                    className="success-item"
                    data-tooltip={link.successItem}
                  >
                    <Icon icon="check" />
                  </div>
                ) : null}
              </div>
            </Link>
          </div>
        )
      )}

      <style jsx>{`
        .toggle {
          align-items: center;
          background-color: transparent;
          border: 0;
          color: ${theme.textColorLighter};
          cursor: pointer;
          display: none;
          font-family: inherit;
          font-size: 1.4rem;
          font-weight: 600;
          outline: 0;
          padding: 12px 15px;
          position: relative;
          text-align: left;
          text-decoration: none;
          transition: background-color 0.3s, color 0.3s;
          width: 100%;
        }
        .link-subheading {
          border-top: 1px solid ${theme.lightGrey};
          color: ${theme.textColorLight};
          display: none;
          font-size: 1.3rem;
          font-weight: 700;
          padding: 15px 18px 10px;
        }
        .link-wrapper {
          border-top: 1px solid ${theme.lightGrey};
          display: none;
          padding: 7px;
          width: 100%;
        }
        .link-wrapper-sublink {
          border-top: 0;
          padding: 0 7px 7px;
        }
        .link {
          align-items: center;
          background-color: transparent;
          border: 0;
          border-radius: ${theme.buttonBorderRadius};
          color: ${theme.textColorLighter};
          cursor: pointer;
          display: flex;
          font-family: inherit;
          font-size: 1.4rem;
          font-weight: 600;
          outline: 0;
          padding: 8px 10px;
          position: relative;
          text-align: left;
          text-decoration: none;
          transition: background-color 0.3s, color 0.3s;
          width: 100%;
        }
        .link:hover,
        .link:focus {
          background-color: ${theme.lightGreyLighter};
        }
        .link.active {
          background-color: ${theme.lightGrey};
          color: ${theme.textColor};
        }
        .is-open .link-subheading,
        .is-open .link-wrapper {
          display: flex;
        }
        .icon {
          display: inline-block;
          font-size: 1.2rem;
          margin-right: 8px;
        }
        .link-title {
          flex-grow: 1;
          min-width: 0;
        }
        .toggle {
          color: ${theme.textColor};
          display: flex;
          font-weight: 700;
        }
        .toggle-icon {
          color: ${theme.textColorLighter};
          font-size: 1.2rem;
          margin-left: 10px;
        }
        .success-item {
          background-color: ${theme.successLightColor};
          border-radius: 10px;
          color: ${theme.successLightTextColor};
          font-size: 1rem;
          margin-left: 10px;
          padding: 3px 0;
          text-align: center;
          width: 18px;
          height: 18px;
        }
        .count-items,
        .unread-items {
          border-radius: 10px;
          font-size: 1.2rem;
          font-weight: 700;
          line-height: 1;
          margin-left: 10px;
          padding: 3px 6px;
          position: relative;
        }
        .count-items {
          background-color: ${theme.lightGrey};
          color: ${theme.textColorLight};
        }
        .unread-items {
          background-color: ${theme.dangerColor};
          color: ${theme.dangerTextColor};
        }
        .count-items-unread {
          background-color: ${theme.dangerColor};
          border-radius: 10px;
          color: ${theme.dangerTextColor};
          font-size: 1rem;
          font-weight: 700;
          line-height: 1;
          padding: 2px 5px;
          position: absolute;
          right: -9px;
          top: -7px;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .toggle {
            display: none;
          }
          .link-subheading:nth-child(2) {
            border-top: none;
            padding-top: 10px;
          }
          .link-wrapper:first-of-type {
            border-top: none;
          }
          .link-subheading,
          .link-wrapper {
            display: flex;
          }
        }
      `}</style>
    </nav>
  );
};

export default PageSidebarNav;
